/**
 * @fileoverview Backward compatibility wrapper for CXGN.Table
 *
 * This factory maintains the original CXGN.Table API while internally
 * using the new modular Table implementation. This ensures existing code
 * continues to work without modification.
 *
 * Usage (maintains old API):
 *   var table = new CXGN.Table({ container: '#my-table', ... });
 *
 * @module TableFactory
 */

import { Table } from './Table.js';

/**
 * Feature flag to enable new implementation
 * Set via environment or runtime configuration
 * @type {boolean}
 */
const USE_NEW_IMPLEMENTATION = typeof window !== 'undefined'
  ? window.CXGN_TABLE_USE_NEW_IMPL !== false
  : true;

/**
 * TableFactory - Backward compatibility wrapper
 * Creates new Table instances while maintaining legacy API surface
 *
 * @class
 * @param {import('./types.js').TableOptions} options - Table configuration
 */
export function TableFactory(options) {
  if (USE_NEW_IMPLEMENTATION) {
    // Use new modular implementation
    return new Table(options);
  } else {
    // Fallback to legacy implementation
    console.warn('CXGN.Table: Using legacy implementation. Set CXGN_TABLE_USE_NEW_IMPL=true to use new version.');

    // This would load the old implementation if needed
    // For now, we'll throw an error since we're migrating
    if (typeof window !== 'undefined' && window.CXGN && window.CXGN.TableLegacy) {
      return new window.CXGN.TableLegacy(options);
    }

    throw new Error('Legacy Table implementation not available. Please update to new API.');
  }
}

/**
 * Initialize global CXGN namespace for backward compatibility
 * This maintains the original global API: new CXGN.Table(options)
 */
export function initGlobalAPI() {
  if (typeof window === 'undefined') {
    return; // Not in browser environment
  }

  // Create CXGN namespace if it doesn't exist
  window.CXGN = window.CXGN || {};

  // Preserve existing CXGN.Table as CXGN.TableLegacy
  if (window.CXGN.Table && !window.CXGN.TableLegacy) {
    console.log('CXGN.Table: Preserving legacy implementation as CXGN.TableLegacy');
    window.CXGN.TableLegacy = window.CXGN.Table;
  }

  // Replace with factory that creates new Table instances
  window.CXGN.Table = function(options) {
    return TableFactory(options);
  };

  // Export Table class directly for ES6 imports
  window.CXGN.Table.Table = Table;

  // Add version info
  window.CXGN.Table.version = '2.0.0';
  window.CXGN.Table.legacy = false;

  console.log('CXGN.Table v2.0.0: Modern implementation loaded');
}

/**
 * Utility to check if new implementation is active
 * @returns {boolean} True if using new implementation
 */
export function isNewImplementation() {
  return USE_NEW_IMPLEMENTATION;
}

/**
 * Migration helper: validates options for compatibility
 * @param {Object} options - Table options
 * @returns {Object} Validated and migrated options
 */
export function migrateOptions(options) {
  const migrated = { ...options };

  // Map legacy option names to new names
  const optionMappings = {
    'fnInitComplete': 'initComplete',
    'fnDrawCallback': 'drawCallback',
    'fnRowCallback': 'rowCallback',
    // Add more mappings as needed
  };

  Object.keys(optionMappings).forEach(oldKey => {
    if (oldKey in migrated && !(optionMappings[oldKey] in migrated)) {
      migrated[optionMappings[oldKey]] = migrated[oldKey];
      delete migrated[oldKey];
      console.warn(`CXGN.Table: Option '${oldKey}' is deprecated. Use '${optionMappings[oldKey]}' instead.`);
    }
  });

  return migrated;
}

// Auto-initialize global API if in browser
// Note: table.js entry point also calls initGlobalAPI(), so we check for existing init
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const doInit = () => {
    // Only init if not already initialized with v2.0.0
    if (!window.CXGN?.Table?.version || window.CXGN.Table.version !== '2.0.0') {
      initGlobalAPI();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', doInit);
  } else {
    setTimeout(doInit, 0);
  }
}
